import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const [tier, setTier] = useState(null); // null = loading, not yet determined
  const [features, setFeatures] = useState(null);
  const [subscription, setSubscription] = useState(null);  // Full subscription info including trial
  const [messageUsage, setMessageUsage] = useState({ used: 0, remaining: -1, limit: -1 });
  const [devices, setDevices] = useState({ count: 0, max: 1, list: [], limit_reached: false });
  const [deviceLimitReached, setDeviceLimitReached] = useState(false);
  const [planNotification, setPlanNotification] = useState(null);
  const [isShareOwner, setIsShareOwner] = useState(false);
  const [isShareMember, setIsShareMember] = useState(false);
  const [shareOwnerName, setShareOwnerName] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const deviceLimitHandledRef = useRef(false);
  const syncDoneRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true, timeout: 8000 });
      const data = res.data;
      setTier(data.tier);
      setFeatures(data.features);
      setSubscription(data.subscription);  // Store full subscription info
      setMessageUsage(data.messages);
      setDevices(data.devices);
      setIsShareOwner(data.is_share_owner === true);
      setIsShareMember(data.is_share_member === true);
      setShareOwnerName(data.share_owner_name || null);
      // Only set device limit if not already handled this session
      if (data.devices?.limit_reached && !deviceLimitHandledRef.current) {
        setDeviceLimitReached(true);
      }
      // Plan notification from admin
      if (data.plan_notification) {
        setPlanNotification(data.plan_notification);
      }
    } catch {
      setTier(prev => prev ?? 'free');
    } finally {
      setLoading(false);
    }
  }, []);

  const dismissPlanNotification = useCallback(async () => {
    setPlanNotification(null);
    try {
      await axios.post(`${API}/subscription/dismiss-notification`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
  }, []);

  const replaceDevice = useCallback(async (targetDeviceId) => {
    try {
      const url = targetDeviceId
        ? `${API}/subscription/devices/replace/${targetDeviceId}`
        : `${API}/subscription/devices/replace-latest`;
      const res = await axios.post(url, {}, { withCredentials: true });
      // Mark as handled so periodic polls don't re-trigger
      deviceLimitHandledRef.current = true;
      setDeviceLimitReached(false);
      await fetchStatus();
      return res.data;
    } catch (err) {
      throw err;
    }
  }, [fetchStatus]);

  const dismissDeviceLimit = useCallback(() => {
    deviceLimitHandledRef.current = true;
    setDeviceLimitReached(false);
  }, []);

  const refreshMessageUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/message-limit`, { withCredentials: true });
      setMessageUsage(res.data);
    } catch {
      // Ignore
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  // Cross-platform subscription sync — runs once on app launch
  useEffect(() => {
    if (tier === null || syncDoneRef.current) return; // Wait for initial status, run only once
    syncDoneRef.current = true;

    const syncSubscriptions = async () => {
      try {
        const res = await axios.post(`${API}/subscription/sync-all-platforms`, {}, {
          withCredentials: true,
          timeout: 15000,
        });
        const data = res.data;
        if (data.tier_changed) {
          // Tier changed — refresh status and notify user
          await fetchStatus();
          const tierNames = { free: 'Free', paid: 'Standard', premium: 'Premium', share: 'Share' };
          const newName = tierNames[data.current_tier] || data.current_tier;
          const prevName = tierNames[data.previous_tier] || data.previous_tier;
          if ((data.current_tier === 'free' && data.previous_tier !== 'free')) {
            toast.info(`Your ${prevName} subscription has expired. You're now on the Free plan.`);
          } else if (data.current_tier !== 'free') {
            toast.success(`Your subscription has been synced to ${newName}.`);
          }
        }
      } catch {
        // Silent fail — sync is best-effort
      }
    };

    syncSubscriptions();
  }, [tier, fetchStatus]);

  useEffect(() => {
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    const onSubChange = () => fetchStatus();
    window.addEventListener('subscription-changed', onSubChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('subscription-changed', onSubChange);
    };
  }, [fetchStatus]);

  const hasFeature = useCallback((feature) => {
    if (tier === null) return true;
    if (!features) return tier !== 'free';
    return features[feature] === true || features[feature] === 'all';
  }, [features, tier]);

  const canSendMessage = useCallback(() => {
    if (messageUsage.limit === -1) return true;
    return messageUsage.remaining > 0;
  }, [messageUsage]);

  const isPaid = tier === 'paid' || tier === 'premium' || tier === 'share';
  const isPremium = tier === 'premium';
  const isSharePlan = tier === 'share';

  return (
    <SubscriptionContext.Provider value={{
      tier, features, subscription, messageUsage, devices, loading,
      isPaid, isPremium, isSharePlan, isShareOwner, isShareMember, shareOwnerName, hasFeature, canSendMessage,
      refreshStatus: fetchStatus, refreshMessageUsage,
      deviceLimitReached, replaceDevice, dismissDeviceLimit,
      planNotification, dismissPlanNotification,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}
